import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import { Button } from './ui/button';

const NAV_ITEMS = [
  { id: 'services', label: 'Servicios' },
  { id: 'process', label: 'Proceso' },
  { id: 'industries', label: 'Sectores' },
  { id: 'faq', label: 'FAQ' },
];

const Header = ({ onBookConsultation }) => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Lock body scroll when mobile menu is open + allow Escape to close
  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && setMobileOpen(false);
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
      window.addEventListener('keydown', onKey);
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKey);
    };
  }, [mobileOpen]);

  const scrollToSection = (sectionId) => {
    setMobileOpen(false);
    const element = document.getElementById(sectionId);
    if (element) element.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <motion.header
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6, ease: 'easeOut' }}
      className={`fixed top-0 left-0 right-0 z-40 transition-all duration-300 ${
        scrolled || mobileOpen
          ? 'bg-white/90 backdrop-blur-lg shadow-sm border-b border-slate-200'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.97 }}
            className="cursor-pointer rounded-lg"
            onClick={() => { setMobileOpen(false); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
            data-testid="header-logo"
            aria-label="Aivanilo — Ir al inicio"
          >
            <img src="/aivanilo-logo.png" alt="Aivanilo" className="h-8 md:h-9 w-auto" width="180" height="40" />
          </motion.button>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8" aria-label="Navegación principal">
            {NAV_ITEMS.map((item) => (
              <button
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                data-testid={`nav-${item.id}`}
                className="relative text-slate-700 hover:text-slate-900 font-medium transition-colors duration-200 after:absolute after:left-0 after:-bottom-1 after:h-0.5 after:w-0 after:bg-blue-600 after:transition-all after:duration-300 hover:after:w-full"
              >
                {item.label}
              </button>
            ))}
            <a
              href="/login"
              data-testid="nav-login"
              className="text-slate-700 hover:text-slate-900 font-medium transition-colors duration-200"
            >
              Acceso clientes
            </a>
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Button
                onClick={onBookConsultation}
                data-testid="header-book-btn"
                className="bg-slate-900 hover:bg-slate-800 active:bg-slate-950 text-white px-6 py-2 rounded-lg transition-colors duration-200 shadow-sm hover:shadow-md"
              >
                Reservar Consulta
              </Button>
            </motion.div>
          </nav>

          {/* Mobile controls */}
          <div className="flex items-center gap-2 md:hidden">
            <Button
              onClick={onBookConsultation}
              size="sm"
              data-testid="header-book-btn-mobile"
              className="bg-slate-900 hover:bg-slate-800 active:bg-slate-950 text-white px-4 py-2 rounded-lg text-sm"
            >
              Reservar
            </Button>
            <button
              onClick={() => setMobileOpen((v) => !v)}
              aria-label={mobileOpen ? 'Cerrar menú' : 'Abrir menú'}
              aria-expanded={mobileOpen}
              aria-controls="mobile-menu"
              data-testid="mobile-menu-toggle"
              className="p-2 rounded-lg text-slate-800 hover:bg-slate-100 transition-colors"
            >
              {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu panel */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.nav
            id="mobile-menu"
            aria-label="Navegación móvil"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25, ease: 'easeInOut' }}
            className="md:hidden overflow-hidden border-t border-slate-200 bg-white"
          >
            <div className="px-6 py-4 flex flex-col gap-1">
              {NAV_ITEMS.map((item) => (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  data-testid={`mobile-nav-${item.id}`}
                  className="text-left text-slate-700 hover:text-slate-900 hover:bg-slate-50 font-medium py-3 px-3 rounded-lg transition-colors"
                >
                  {item.label}
                </button>
              ))}
              <a
                href="/login"
                data-testid="mobile-nav-login"
                className="text-left text-slate-700 hover:text-slate-900 hover:bg-slate-50 font-medium py-3 px-3 rounded-lg transition-colors"
              >
                Acceso clientes
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export default Header;
